import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, TextField, Tooltip, Typography } from '@mui/material';
import parse, { DOMNode } from 'html-react-parser';
import { useConfirm } from 'material-ui-confirm';
import { useQueryClient } from 'react-query';
import ContentEditor from 'components/ContentEditor';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import { translate as t } from 'hooks/useTranslations';
import { Delete } from '@mui/icons-material';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';
import { enqueueSnackbar } from 'notistack';
import ContentConversionHelper from 'views/App/Portal/Policy/DraftManuals/helpers/contentConversionHelper';
import useAccreditationStandardById from '../../../../_data_sources/AccreditationStandards/useStandard';
import { updateStandard, destroyStandard } from '../../../../_data_sources/AccreditationStandards/manageStandard';
import { useStandardsContext } from '../context';
import type { AccreditationStandardNew } from '../../../../_types/awb';

export type StandardMode = 'EDITABLE' | 'EDITING';

export interface StandardTabContentRef {
  save: () => void;
  startEditing: () => void;
  cancelEditing: () => void;
}

export interface StandardTabContentProps {
  standardId: string;
  onModeChange: (mode: StandardMode) => void;
  onSavingChange?: (isSaving: boolean) => void;
}

const StandardTabContent = forwardRef<StandardTabContentRef, StandardTabContentProps>(
  function StandardTabContent({ standardId, onModeChange, onSavingChange }, ref) {
    const confirm = useConfirm();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { id: accreditationId, versionId } = useParams();
    const { getStandardType } = useStandardsContext();

    const [heading, setHeading] = useState('');
    const [title, setTitle] = useState('');
    const [draftContent, setDraftContent] = useState('');
    const [mode, setMode] = useState<StandardMode>('EDITABLE');
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const standardByIdQuery = useAccreditationStandardById(
      accreditationId!,
      versionId!,
      standardId,
      undefined,
      { enabled: !isDeleting && !!standardId && !!accreditationId && !!versionId }
    );
    const isLoading = standardByIdQuery.isLoading || standardByIdQuery.isFetching;
    const standardData = standardByIdQuery.data;

    useEffect(() => {
      if (!isLoading && standardData) {
        setDraftContent(standardData.draftContent ?? '');
        setHeading(standardData.heading ?? '');
        setTitle(standardData.title ?? '');
      }
    }, [standardId, standardData, isLoading]);

    useEffect(() => {
      onModeChange(mode);
    }, [mode, onModeChange]);

    useEffect(() => {
      onSavingChange?.(isSaving);
    }, [isSaving, onSavingChange]);

    const standardTypeText =
      standardId && getStandardType
        ? (() => {
            const type = getStandardType(standardId);
            switch (type) {
              case 'subStandard':
                return t('awb.standards.new.subStandard');
              case 'requirement':
                return t('awb.standards.new.requirement');
              default:
                return t('awb.standards.new.standard');
            }
          })()
        : t('awb.standards.new.standard');

    const handleSave = useCallback(async () => {
      if (!standardData || !accreditationId || !versionId) return;
      const saveProcess = async () => {
        setIsSaving(true);
        const payload: AccreditationStandardNew = {
          ...standardData,
          position: Math.max(standardData.position ?? 1, 1),
          heading,
          title,
          draftContent: ContentConversionHelper.contentForRender(draftContent),
        } as AccreditationStandardNew;
        try {
          await updateStandard(accreditationId, versionId, standardId, payload, async () => {
            await queryClient.invalidateQueries(['accreditation_standards', accreditationId, versionId]);
            await queryClient.invalidateQueries(['accreditation_standard', accreditationId, versionId, standardId]);
            if (standardData.parentStandardId) {
              await queryClient.invalidateQueries(['accreditation_standards', accreditationId, versionId]);
            }
            await standardByIdQuery.refetch();
            setMode('EDITABLE');
            enqueueSnackbar(t('awb.standards.detail.saveSuccess', '', { standardType: standardTypeText }), {
              variant: 'success',
              autoHideDuration: 6000,
            });
          });
        } catch (err) {
          let errorToPass: ServerErrorResponse;
          if (err instanceof Error && err.message) {
            try {
              errorToPass = JSON.parse(err.message);
            } catch {
              errorToPass = {
                message: err.message,
                errorInformationCode: undefined,
                detail: err.message,
                errorDetails: [],
              } as ServerErrorResponse;
            }
          } else {
            errorToPass = err as ServerErrorResponse;
          }
          errorHelper.enqueueErrors(errorToPass);
        } finally {
          setIsSaving(false);
        }
      };
      if (standardData.publishedState === 'PUBLISHED') {
        confirm({
          title: 'Are you sure you want to proceed?',
          description: 'Changes could impact live accreditation customers.',
          cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
          confirmationButtonProps: { variant: 'contained', color: 'error' },
          confirmationText: 'Update',
          cancellationText: 'Cancel',
        })
          .then(saveProcess)
          .catch(() => {});
      } else {
        await saveProcess();
      }
    }, [
      standardData,
      heading,
      title,
      draftContent,
      standardTypeText,
      accreditationId,
      versionId,
      standardId,
      confirm,
      queryClient,
      standardByIdQuery,
    ]);

    const handleDelete = () => {
      confirm({
        title: t('awb.standards.detail.deleteConfirm.title', '', { standardType: standardTypeText }),
        description: t('awb.standards.detail.deleteConfirm.description', '', { standardType: standardTypeText }),
        cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
        confirmationButtonProps: { variant: 'contained', color: 'error' },
        confirmationText: t('awb.standards.detail.deleteConfirm.confirm'),
        cancellationText: t('awb.standards.detail.deleteConfirm.cancel'),
      })
        .then(async () => {
          if (!standardData || !accreditationId || !versionId) return;
          setIsDeleting(true);
          await destroyStandard(
            accreditationId,
            versionId,
            standardId,
            async () => {
              await queryClient.invalidateQueries(['accreditation_standards', accreditationId, versionId]);
              if (standardData.parentStandardId) {
                await queryClient.invalidateQueries([
                  'accreditation_standards',
                  accreditationId,
                  versionId,
                  standardData.parentStandardId,
                ]);
              }
              await queryClient.refetchQueries(['accreditation_standards', accreditationId, versionId]);
              enqueueSnackbar(t('awb.standards.detail.deleteSuccess', '', { standardType: standardTypeText }), {
                variant: 'success',
              });
              if (standardData.parentStandardId) {
                navigate(
                  `/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards/${standardData.parentStandardId}`
                );
              } else {
                navigate(`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards`);
              }
            },
            (err) => {
              errorHelper.enqueueErrors(err as ServerErrorResponse);
              setIsDeleting(false);
            }
          );
        })
        .catch(() => {});
    };

    const startEditing = useCallback(() => setMode('EDITING'), []);

    const cancelEditing = useCallback(() => {
      if (standardData) {
        setDraftContent(standardData.draftContent ?? '');
        setHeading(standardData.heading ?? '');
        setTitle(standardData.title ?? '');
      }
      setMode('EDITABLE');
    }, [standardData]);

    useImperativeHandle(
      ref,
      () => ({
        save: handleSave,
        startEditing,
        cancelEditing,
      }),
      [handleSave, startEditing, cancelEditing]
    );

    const onSaveContent = (content: string) => setDraftContent(content);

    return (
      <>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Box>
            {mode === 'EDITING' ? (
              <Stack spacing={3}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant='eyebrowMd'>{standardTypeText}</Typography>
                  {standardData?.publishedState !== 'PUBLISHED' && (
                    <Tooltip
                      title={
                        standardData && standardData.standardCount > 0
                          ? 'This standard cannot be deleted while it has substandards'
                          : t('awb.standards.detail.delete', '', { standardType: standardTypeText })
                      }
                    >
                      <span>
                        <Button
                          variant='outlined'
                          color='neutral'
                          size='small'
                          className='xs icon-only'
                          onClick={handleDelete}
                          disabled={!!(standardData && standardData.standardCount > 0)}
                        >
                          <Delete />
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', width: '100%', gap: 2, mb: 4 }}>
                  <TextField
                    placeholder='Enter #'
                    hiddenLabel
                    value={heading}
                    onChange={(e) => setHeading(e.target.value)}
                    sx={{ width: '15%', pt: 0 }}
                  />
                  <TextField
                    placeholder='Enter Standard Title'
                    hiddenLabel
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    sx={{ width: '85%', pt: 0 }}
                  />
                </Box>
                <Stack spacing={3}>
                  <ContentEditor
                    id={`content_${standardId}`}
                    key={`content_${standardId}`}
                    contentHTML={draftContent}
                    onSave={onSaveContent}
                    isEditable
                    trackChangesEnabled={false}
                    textEntitiesEnabled={false}
                    crossReferencesEnabled={false}
                    imagesEnabled={false}
                    attachmentsEnabled={false}
                  />
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={3}>
                <Typography variant='eyebrowMd'>{standardTypeText}</Typography>
                <Typography variant='subtitleMd'>{`${heading} ${title}`}</Typography>
                <Box sx={{ py: 3, minHeight: 0, overflow: 'auto' }}>
                  {parse(draftContent ?? '', {
                    replace(node: DOMNode, ind: number) {
                      return PolicyContentHelper.handleContent(node, ind);
                    },
                  })}
                </Box>
              </Stack>
            )}
          </Box>
        )}
      </>
    );
  }
);

export default StandardTabContent;
